import { useMutation } from "@apollo/client";
import { Button, MenuItem, Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { DiscrepancyResolutionPath } from "generated-graphql/graphql";
import { useState } from "react";
import { EXCEEDANCE_REFETCH } from "routes/Staff/GeneratorStatusChanges/exceedanceKeys";

import { DISCREPANCY_MUTATION_REFETCH_QUERIES } from "./discrepancyRefetchQueries";
import { ManifestCorrectionValue } from "./ManifestCorrectionValue";

const COMPLETE_CORRECTION = gql(`
  mutation CompleteStaffManifestCorrection($discrepancyId: ID!, $resolutionPath: DiscrepancyResolutionPath!, $note: String!) {
    completeStaffManifestCorrection(discrepancyId: $discrepancyId, resolutionPath: $resolutionPath, note: $note) {
      id
      status
      resolutionPath
      resolutionNote
      resolvedAt
    }
  }
`);

const CORRECTED_IN = [
  { value: DiscrepancyResolutionPath.Emanifest, label: "e-Manifest" },
  { value: DiscrepancyResolutionPath.VendorData, label: "Vendor data" },
  { value: DiscrepancyResolutionPath.ManualEntry, label: "Manual entry" },
];

type Correction = {
  id: string;
  field: string;
  status?: string | null;
  staffReported?: boolean;
  correctValue?: string | null;
};

/** Caller must be a staff-only surface; the mutation separately enforces staff access. */
export function CompleteStaffCorrectionButton({
  correction,
  disabled,
}: {
  correction: Correction;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (
    !correction.staffReported ||
    (correction.status !== "UNRESOLVED" && correction.status !== "PENDING")
  )
    return null;
  return (
    <>
      <Button size="small" disabled={disabled} onClick={() => setOpen(true)}>
        Mark fixed
      </Button>
      {open && (
        <CompleteStaffCorrectionDialog correction={correction} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function CompleteStaffCorrectionDialog({
  correction,
  onClose,
}: {
  correction: Correction;
  onClose: () => void;
}) {
  const [path, setPath] = useState<DiscrepancyResolutionPath>(DiscrepancyResolutionPath.Emanifest);
  const [note, setNote] = useState("");
  const alerts = useAlerts();
  const [complete, { loading }] = useMutation(COMPLETE_CORRECTION, {
    refetchQueries: [
      ...DISCREPANCY_MUTATION_REFETCH_QUERIES,
      ...EXCEEDANCE_REFETCH,
      "ManifestReviewDiscrepancies",
      "StaffManifestReviewManifests",
      "GeneratorStatusExceedanceLinkableManifestCorrections",
    ],
    awaitRefetchQueries: true,
  });
  const handleComplete = async () => {
    try {
      await complete({
        variables: { discrepancyId: correction.id, resolutionPath: path, note: note.trim() },
      });
      alerts.success("Correction marked fixed.");
      onClose();
    } catch (e) {
      alerts.error("Failed to mark the correction fixed.", e);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title="Mark correction fixed"
      description="Confirm that the corrected value has been applied to the manifest. This closes the correction and updates findings waiting on it."
      primaryActionButtonProps={{
        onClick: handleComplete,
        loading,
        saveText: "Mark fixed",
        hideStartIcon: true,
        disabled: !note.trim() || loading,
      }}
      cancelButtonProps={{ disabled: loading }}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {correction.correctValue && (
          <ManifestCorrectionValue field={correction.field} value={correction.correctValue} />
        )}
        <TextField
          select
          label="Corrected in"
          value={path}
          size="small"
          required
          onChange={(e) => {
            const selected = CORRECTED_IN.find((option) => option.value === e.target.value);
            if (selected) setPath(selected.value);
          }}
        >
          {CORRECTED_IN.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          size="small"
          required
          multiline
          minRows={2}
        />
      </Stack>
    </Dialog>
  );
}
